/** @jsx jsx */
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import cloneDeep from 'lodash/cloneDeep';
import debounce from 'lodash/debounce';
import { jsx } from '@emotion/core';
import { Text } from '@sitecore-jss/sitecore-jss-react';
import { Row, Column, MediaQueries } from '../../../../styles/Grid';
import { hideElementCss } from '../../../../utils/responsive';
import { CLOUDINARY_BASE_VALUES } from '../../../../utils/constants';
import { Icon } from '../../../ui/Icon';
import Picture from '../../../ui/Picture';
import { VIEWPORTS, Breakpoints } from '../../../../styles/Grid/GridSizes';
import { SafePureComponent } from '../../../../utils/SafePureComponent';
import { buttonReset } from '../../../../styles/Global';
import LinkWrapper from '../../../ui/LinkWrapper';
import {
  contentTabItemCss,
  contentHeadlineCss,
  contentDescriptionCss,
  contentListCss,
  itemContentCss,
  itemTextContentCss,
  iconContainerCss,
  previewImageStyleIcon,
  iconCss,
  viewMoreCss,
  viewMoreTextCss,
} from './index.styles';

const iconImageSizes = {
  [Breakpoints.sm]: {
    ...CLOUDINARY_BASE_VALUES,
    aspectRatio: '1:1',
    width: 16,
    height: 16,
  },
  [Breakpoints.md]: {
    ...CLOUDINARY_BASE_VALUES,
    aspectRatio: '1:1',
    width: 16,
    height: 16,
  },
  [Breakpoints.lg]: {
    ...CLOUDINARY_BASE_VALUES,
    aspectRatio: '1:1',
    width: 16,
    height: 16,
  },
  [Breakpoints.xl]: {
    ...CLOUDINARY_BASE_VALUES,
    aspectRatio: '1:1',
    width: 16,
    height: 16,
  },
};

const mobileDefaultItemsToRender = 3;

class TabContent extends SafePureComponent {
  state = {
    isViewingMore: false,
    numberOfItemsToRender: mobileDefaultItemsToRender,
  };

  onClickViewMore = () => {
    this.setState({
      isViewingMore: true,
      numberOfItemsToRender: get(
        this.props,
        'content.value.itemList.length',
        3,
      ),
    });
  };

  componentDidMount() {
    this.onResizeTabContent();
    window.addEventListener('resize', debounce(this.onResizeTabContent, 80));
  }

  componentWillUnmount() {
    window.removeEventListener('resize', debounce(this.onResizeTabContent, 80));
  }

  onResizeTabContent = () => {
    if (window.innerWidth >= Breakpoints[VIEWPORTS.md]) {
      this.setState({
        numberOfItemsToRender: get(
          this.props,
          'content.value.itemList.length',
          10,
        ),
      });
    } else {
      this.setState({
        numberOfItemsToRender: mobileDefaultItemsToRender,
        isViewingMore: false,
      });
    }
  };

  renderListItem = (item, index) => {
    const { numberOfItemsToRender } = this.state;

    if (index >= numberOfItemsToRender) {
      return;
    }

    return (
      <li css={itemContentCss} key={`items-solution-tabs-${index}`}>
        <span css={iconContainerCss}>
          <Picture
            pictureCss={previewImageStyleIcon}
            image={item.Icon}
            imageSizes={iconImageSizes}
            editable={false}
          />
        </span>
        <p css={itemTextContentCss}>
          <Text field={item.Name} editable={false} />
        </p>
      </li>
    );
  };

  safeRender() {
    const { content, active, t, className } = this.props;
    const { isViewingMore } = this.state;
    const CTA = cloneDeep(content.value.CTA);
    CTA.value.text = `${t('MoreAboutCTA')}`;
    CTA.value.target = '_self';
    const items = get(content, 'value.itemList');
    const componentName = get(this.props, 'rendering.componentName', '');

    return (
      <div className={className} css={contentTabItemCss} data-active={active}>
        <Row>
          <Column
            cols={{ sm: 6, md: 4, lg: 6 }}
            offsets={{ sm: 0, md: 4, lg: 5 }}
          >
            <Row>
              <Column cols={{ sm: 6, md: [4, 4], lg: [5, 6] }}>
                <h2 css={contentHeadlineCss}>
                  <Text field={content.value.Headline} />
                </h2>
              </Column>
            </Row>
            <Row>
              <Column cols={{ sm: 6, md: [4, 4], lg: [5, 6] }}>
                <p css={contentDescriptionCss}>
                  <Text field={content.value.Description} />
                </p>
              </Column>
            </Row>
            <Row>
              <Column cols={{ sm: 6, md: 8 }}>
                {!isEmpty(items) && (
                  <ul css={contentListCss}>
                    {items.map(this.renderListItem)}
                    {!isViewingMore && (
                      <button
                        css={[
                          buttonReset,
                          hideElementCss(MediaQueries.mdAndUp),
                          viewMoreCss,
                        ]}
                        onClick={this.onClickViewMore}
                      >
                        <span css={viewMoreTextCss}>{t('ViewMore')}</span>
                        <Icon name="chevron-down" customCss={iconCss} />
                      </button>
                    )}
                  </ul>
                )}
              </Column>
            </Row>
            <Row>
              <Column cols={{ sm: 6, md: [4, 4], lg: [5, 6], xl: [3, 6] }}>
                <LinkWrapper field={CTA} componentName={componentName} />
              </Column>
            </Row>
          </Column>
        </Row>
      </div>
    );
  }
}

TabContent.displayName = 'SolutionTabs/TabContent';

TabContent.defautProps = {
  t: key => key,
};

export default TabContent;
